// frontend/src/components/TaskModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-07
// Updated:      2026-10-07 15:18 BST
//
// Description:
//   Canonical runtime Task execution modal. PWA/mobile friendly and reusable from
//   Tasks widgets, Job/Work Package progress surfaces and other runtime launchers.
//
// Change Log:
//   2026-10-07: Initial Phase 1C operational task UI. Supports runtime read,
//               manual completion, approval/rejection, defer and unable-to-complete.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const statusLabel = (value) => String(value || "pending").replace(/_/g, " ");

const TaskModal = ({ open, taskContext, onClose, onChanged }) => {
  const { theme } = useTheme();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const { jobId, workPackageId, taskId } = taskContext || {};

  const load = useCallback(async () => {
    if (!open || !jobId || !workPackageId || !taskId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(
        `/jobs/${jobId}/work-packages/${workPackageId}/workflow`,
        { baseURL: "/api" }
      );
      setWorkflow(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unable to load task.");
    } finally {
      setLoading(false);
    }
  }, [open, jobId, workPackageId, taskId]);

  useEffect(() => { load(); }, [load]);

  const task = useMemo(() => {
    const steps = workflow?.steps || [];
    for (const step of steps) {
      const found = (step.tasks || []).find((row) => Number(row.id) === Number(taskId));
      if (found) return { ...found, step };
    }
    return null;
  }, [workflow, taskId]);

  if (!open) return null;

  const mutate = async (path, payload) => {
    setBusy(true);
    setError("");
    try {
      await axiosInstance.post(path, payload, { baseURL: "/api" });
      setNote("");
      await load();
      onChanged?.();
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Task action failed.");
    } finally {
      setBusy(false);
    }
  };

  const base = `/jobs/${jobId}/work-packages/${workPackageId}/tasks/${taskId}`;
  const isApproval = task?.task_type === "approval" || task?.step?.completion_mode === "approval";
  const canAct = task && !["completed", "cancelled"].includes(String(task.status).toLowerCase());

  const deferTask = () => {
    const reason = window.prompt("Why is this task being deferred?");
    if (!reason?.trim()) return;
    const until = window.prompt("Defer until (YYYY-MM-DD HH:MM), or leave blank:") || "";
    mutate(`${base}/execution-exceptions`, {
      exception_type: "deferred",
      reason_code: "operator_deferred",
      reason_text: reason.trim(),
      defer_until: until.trim() ? new Date(until.trim()).toISOString() : null,
      wider_availability_impact: "task_only",
    });
  };

  const unableTask = () => {
    const reason = window.prompt("Why are you unable to complete this task?");
    if (!reason?.trim()) return;
    mutate(`${base}/execution-exceptions`, {
      exception_type: "unable_to_complete",
      reason_code: "operator_unable_to_complete",
      reason_text: reason.trim(),
      defer_until: null,
      wider_availability_impact: "unknown",
    });
  };

  return (
    <div className="fixed inset-0 z-[12000] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" role="dialog" aria-modal="true">
      <div className="bg-white text-gray-900 w-full md:max-w-3xl h-[96dvh] md:h-auto md:max-h-[90vh] rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-4 py-3 text-white flex items-center justify-between gap-3" style={{ background: theme?.accent_color || "#374151" }}>
          <div className="min-w-0">
            <div className="font-semibold truncate">{task?.title || "Task"}</div>
            <div className="text-xs opacity-90 truncate">{workflow?.workflow_name_snapshot || "Runtime workflow"}</div>
          </div>
          <button type="button" onClick={onClose} className="min-w-11 min-h-11 rounded-full hover:bg-white/10 text-xl" aria-label="Close task">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
          {loading && <div className="text-sm text-gray-500">Loading task…</div>}
          {error && <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 p-3 text-sm">{String(error)}</div>}
          {!loading && !task && !error && <div className="text-sm text-gray-500">Task was not found in this runtime workflow.</div>}

          {task && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">Status</div><div className="font-semibold capitalize">{statusLabel(task.status)}</div></div>
                <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">Task Ref</div><div className="font-semibold break-words">{task.task_ref}</div></div>
                <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">Assignee</div><div className="font-semibold">{task.assigned_resource_id ? `Resource ${task.assigned_resource_id}` : "Unassigned"}</div></div>
                <div className="rounded-lg border p-3"><div className="text-xs text-gray-500">Due</div><div className="font-semibold">{task.due_at ? new Date(task.due_at).toLocaleString("en-GB") : "—"}</div></div>
              </div>

              <section className="rounded-xl border p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Task</div>
                <div className="font-semibold text-lg">{task.title}</div>
                {task.description && <div className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{task.description}</div>}
                {task.waiting_reason && <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">Waiting: {task.waiting_reason}</div>}
              </section>

              <section>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="w-full rounded-lg border border-gray-300 p-3 text-base" placeholder="Optional task note" />
              </section>
            </>
          )}
        </div>

        {task && canAct && (
          <div className="border-t bg-white p-3 md:p-4 sticky bottom-0">
            <div className="grid grid-cols-2 md:flex md:justify-end gap-2">
              <button type="button" disabled={busy} onClick={unableTask} className="min-h-11 rounded-lg border border-red-300 px-4 text-red-700">Unable to Complete</button>
              <button type="button" disabled={busy} onClick={deferTask} className="min-h-11 rounded-lg border border-amber-300 px-4 text-amber-800">Defer</button>
              {isApproval ? (
                <>
                  <button type="button" disabled={busy} onClick={() => mutate(`${base}/reject`, { decision_note: note || null })} className="min-h-11 rounded-lg border border-red-300 px-4 text-red-700">Reject</button>
                  <button type="button" disabled={busy} onClick={() => mutate(`${base}/approve`, { decision_note: note || null })} className="min-h-11 rounded-lg px-5 text-white" style={{ background: theme?.button_action || theme?.accent_color || "#2563eb" }}>Approve</button>
                </>
              ) : (
                <button type="button" disabled={busy || task.status !== "ready"} onClick={() => mutate(`${base}/complete`, { completion_note: note || null })} className="col-span-2 min-h-11 rounded-lg px-5 text-white disabled:opacity-50" style={{ background: theme?.button_action || theme?.accent_color || "#2563eb" }}>Complete Task</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskModal;
